import { DefaultItem } from 'features/hawk-recommendations/items/commerce/Item';
import React, { useEffect, useRef } from 'react';
import { Item as ItemModel } from 'shared/components/hawksearch/models/Recommendation/Item';
import Spinner from 'shared/components/Spinner';
import { useInViewOnce } from 'shared/hooks/useInViewOnce';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import type { TinySliderInstance, TinySliderSettings } from 'tiny-slider';
import uuid from 'uuid/v4';
import { getCustomWidgetItemComponent } from '../../items/strategy';
import { ItemProps, ItemStrategyProps } from '../../items/types';
import { WidgetProps } from '../types';

/**
 * Renders a widget for the recommended products returned from hawk api.
 */
export function Carousel(props: WidgetProps) {
	const { widgetData: widget, renderHTML, requestId, attributes } = props;

	const currentInstanceId = useRef('hawk-recommendations-slider-' + uuid()).current;
	const sliderRef = useRef<TinySliderInstance | null>(null);

	const [ref, inView] = useInViewOnce({
		threshold: 0,
	});

	const tnsPromise = import('tiny-slider/src/tiny-slider').then((module) => module.tns);

	// bind tiny-slider on startup or resize
	const initializeSlider = () => {
		if (sliderRef.current) {
			sliderRef.current.destroy();
		}

		(async () => {
			const currentSliderDiv = document.getElementById(currentInstanceId);
			const tns = await tnsPromise;

			const getTinySliderInstance = () => {
				const options: TinySliderSettings = {
					container: `#${currentInstanceId}`,
					items: 1,
					slideBy: 'page',
					controls: false,
					mouseDrag: true,
					gutter: 15,
					lazyload: true,
					loop: false,
					responsive: {
						768: {
							items: 2,
						},
						1024: {
							items: 4,
						},
						1200: {
							items: 5,
						},
					},
				};

				return tns(options);
			};

			if (currentSliderDiv) {
				sliderRef.current = getTinySliderInstance();
			}

			// if there's a re-render, or possibly hmr, make sure to destroy the instance
			// to avoid memory leaks.
			return () => {
				if (sliderRef.current) {
					sliderRef.current.destroy();
				}
			};
		})();
	};

	// Bind tiny-slider on startup
	useEffect(() => {
		initializeSlider();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView]);

	if (!inView) {
		return <div ref={ref} />;
	}

	if (renderHTML && widget.html) {
		return <div dangerouslySetInnerHTML={{ __html: widget.html }} />;
	}

	if (widget.recommendationItems.length == 0) {
		return null;
	}

	// function for dispatching hawk-recommendation-click event
	// this is handled by hawksearch-commerce-tracker to report click event to Hawk Tracking API
	const dispatchWidgetClickEvent = (item: ItemModel, widgetGuid: string) => {
		if (item.id) {
			dispatchRocEvent('hawk-recommendation-click', {
				itemIndex: item.itemIndex,
				requestId: requestId,
				uniqueId: item.id,
				widgetGuid: widgetGuid,
			});
		}
	};

	return (
		<React.Suspense fallback={<Spinner />}>
			<h3 className="roc-visually-hidden" dangerouslySetInnerHTML={{ __html: widget.widgeTitle }} />
			<div ref={ref} className="roc-product-list">
				<div id={currentInstanceId} className="roc-product-list__track">
					{widget.recommendationItems.map((item, key) => {
						const ItemComponent = getWidgetItemComponent({
							widgetGuid: widget.widgetGuid,
							item,
							attributes,
							widgetComponentType: Carousel,
						});

						return (
							<ItemComponent
								key={key}
								item={item}
								classNameSuffix={widget.widgetCssClass}
								onClick={() => dispatchWidgetClickEvent(item, widget.widgetGuid)}
							/>
						);
					})}
				</div>
			</div>
		</React.Suspense>
	);
}

/**
 * This is the default strategy for picking out an item type. It only checks if a custom one has been defined,
 * and if that returns anything, that component is returned. Otherwise, we just return the default component.
 * If you need to customize this, change `getCustomWidgetItemComponent`
 */
function getWidgetItemComponent(props: ItemStrategyProps): React.ComponentType<ItemProps> {
	const customComponent = getCustomWidgetItemComponent(props);

	if (customComponent !== undefined) {
		return customComponent;
	}

	return DefaultItem;
}
